import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendVerifySms, verifySms } from 'api/verification';
import * as VerificationStep from 'assets/js/pages/account-settings/account-about/VerificationStep';
import * as Form from 'components/form';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { GivebutterAxiosError } from 'types/error';

interface PhoneVerificationFormValues {
    smsCode: string;
}

const PhoneVerification = () => {
    const toast = useToast();
    const { phone, id: userId, hasSmsCode, isPhoneVerified } = useCurrentUser();
    const { apiUrl } = useAppState();
    const { mutateAsync: invalidateUserData } = useInvalidateUserAndAccount();
    const [resentSms, setResentSms] = useState(false);
    const smsInputRef = useRef<HTMLInputElement>(null);

    const methods = Form.useGivebutterForm<PhoneVerificationFormValues>({
        defaultValues: { smsCode: '' },
        mode: 'onTouched',
    });
    const { control, formState, path, handleSubmit, setError, reset } = methods;

    const { mutate: sendSms, isPending: isSendingSms } = useMutation<unknown, GivebutterAxiosError>(
        {
            mutationFn: () => sendVerifySms(userId),
            onSuccess: () => {
                toast.success('SMS verification sent!');
                setResentSms(true);
            },
            onError: error => {
                toast.error(
                    error.response?.data.message || 'Something went wrong. Please try again.',
                );
            },
        },
    );

    const { mutate: verify, isPending: isVerifying } = useMutation<
        unknown,
        GivebutterAxiosError,
        PhoneVerificationFormValues
    >({
        mutationFn: ({ smsCode }) => verifySms(userId, smsCode),
        onSuccess: async () => {
            await invalidateUserData();
            toast.success('Your phone is now verified.');
        },
        onError: error => {
            const errors = error.response?.data.errors;
            if (errors) {
                for (let i in errors) {
                    setError('smsCode', {
                        message: errors[i][0],
                    });
                }
            } else {
                toast.error(
                    error.response?.data.message || 'Something went wrong. Please try again.',
                );
            }
        },
    });

    useEffect(() => {
        if (
            formState.isSubmitted ||
            formState.isSubmitting ||
            !formState.isValid ||
            !formState.dirtyFields.smsCode
        ) {
            return;
        }

        handleSubmit(values => verify(values))();
    }, [formState, handleSubmit, verify]);

    return (
        <VerificationStep.Root isCompleted={isPhoneVerified}>
            <VerificationStep.Heading>
                <VerificationStep.HeadingIcon />
                <VerificationStep.HeadingContent>
                    <VerificationStep.HeadingStep>Step 2</VerificationStep.HeadingStep>
                    <VerificationStep.HeadingTitle>
                        Verify your phone
                        <VerificationStep.HeadingSubtitle>
                            {phone ? getFormattedPhoneNumber(phone) : null}
                        </VerificationStep.HeadingSubtitle>
                    </VerificationStep.HeadingTitle>
                </VerificationStep.HeadingContent>
                <VerificationStep.HeadingLabel className="text-orange-500">
                    Recommended
                </VerificationStep.HeadingLabel>
            </VerificationStep.Heading>

            <VerificationStep.Content>
                <p className="text-sm text-gray-700">
                    Click the button below to receive a 6 digit code to your{' '}
                    {phone ? `phone: ${getFormattedPhoneNumber(phone)}` : 'phone'}. If you haven't
                    received the text after a few minutes, click the button below once more.
                </p>

                {(hasSmsCode || resentSms) && !!phone && (
                    <Form.Provider {...methods}>
                        <form onSubmit={handleSubmit(values => verify(values))}>
                            <Form.Container className="max-w-sm">
                                <Form.Label name={path('smsCode')} className="sr-only">
                                    SMS Code
                                </Form.Label>
                                <Form.TextInputRoot
                                    disabled={isVerifying}
                                    control={control}
                                    name={path('smsCode')}
                                    rules={{
                                        required: 'SMS Code is required',
                                        minLength: {
                                            value: 6,
                                            message: 'SMS Code must be 6 characters in length',
                                        },
                                        maxLength: {
                                            value: 6,
                                            message: 'SMS Code must be 6 characters in length',
                                        },
                                    }}
                                >
                                    {isVerifying && (
                                        <FontAwesomeIcon
                                            className="animate-spin"
                                            icon={faSpinnerThird}
                                        />
                                    )}
                                    <Form.TextInputForm
                                        ref={smsInputRef}
                                        placeholder="SMS Code"
                                        onFocus={e => e.target.select()}
                                        autoComplete="one-time-code"
                                        autoFocus
                                        autoCapitalize="false"
                                    />
                                </Form.TextInputRoot>
                                <Form.Error name={path('smsCode')} />
                            </Form.Container>
                        </form>
                    </Form.Provider>
                )}

                {!phone ? (
                    <AlertNotification.Root type="error">
                        <AlertNotification.Icon />
                        <AlertNotification.Content>
                            <AlertNotification.Title>
                                You need to{' '}
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`${apiUrl}/profile`}
                                >
                                    add a phone number
                                </a>{' '}
                                before you can proceed.
                            </AlertNotification.Title>
                        </AlertNotification.Content>
                    </AlertNotification.Root>
                ) : null}

                <div className="flex flex-wrap items-center gap-4">
                    <Button
                        variant="solid"
                        disabled={isSendingSms || isVerifying || !phone}
                        onClick={() => {
                            sendSms();
                            reset();
                        }}
                    >
                        {isSendingSms ? (
                            <FontAwesomeIcon className="animate-spin" icon={faSpinnerThird} />
                        ) : null}
                        Send text code
                    </Button>
                    <a href={`${apiUrl}/profile`} target="_blank" rel="noreferrer noopener">
                        Edit your phone number
                    </a>
                </div>

                <p className="text-xs">
                    <em>
                        Note: Please make sure to use a mobile phone that can receive texts. This
                        number will not be displayed anywhere publicly and is only used for identity
                        verification.
                    </em>
                </p>
            </VerificationStep.Content>
        </VerificationStep.Root>
    );
};

export { PhoneVerification };
